import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database';

type ListeningSession = Database['public']['Tables']['listening_sessions']['Row'];
type ListeningSessionInsert = Database['public']['Tables']['listening_sessions']['Insert'];
type ListeningSessionUpdate = Database['public']['Tables']['listening_sessions']['Update'];

export interface StartListeningParams {
  userId: string;
  albumId: string;
  trackId: string;
}

export interface EndListeningParams {
  sessionId: string;
  durationSeconds: number;
  completed?: boolean;
  skipped?: boolean;
}

export interface ListeningSessionService {
  startListening: (params: StartListeningParams) => Promise<{ data: ListeningSession | null; error: string | null }>;
  endListening: (params: EndListeningParams) => Promise<{ data: ListeningSession | null; error: string | null }>;
}

export const listeningSessionService: ListeningSessionService = {
  startListening: async ({ userId, albumId, trackId }) => {
    try {
      const now = new Date().toISOString();

      const payload: ListeningSessionInsert = {
        user_id: userId,
        album_id: albumId,
        track_id: trackId,
        started_at: now,
        completed: false,
        skipped: false,
      };

      const { data, error } = await supabase
        .from('listening_sessions')
        .insert(payload)
        .select()
        .single();

      if (error) {
        console.error('Error starting listening session:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start listening session';
      console.error('Error starting listening session:', err);
      return { data: null, error: errorMessage };
    }
  },

  endListening: async ({ sessionId, durationSeconds, completed = false, skipped = false }) => {
    try {
      const now = new Date().toISOString();

      const updates: ListeningSessionUpdate = {
        ended_at: now,
        duration_seconds: Math.max(0, durationSeconds),
        completed,
        skipped,
      };

      const { data, error } = await supabase
        .from('listening_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single();

      if (error) {
        console.error('Error ending listening session:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end listening session';
      console.error('Error ending listening session:', err);
      return { data: null, error: errorMessage };
    }
  },
};
